import { Component, EventEmitter, forwardRef, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validator, ValidatorFn } from "@angular/forms";
import { Observable, of, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { AttoService } from "@services/atto.service";
import { StatoAttoEnum } from "@enum/stato-atto-enum";
import { TipologiaDecretoEnum } from "@enum/tipologia-decreto-enum";
import { ModaleAggiungiDatiDecretoComponent } from "@app/shared/components/modale-aggiungi-dati-decreto/modale-aggiungi-dati-decreto.component";
import { FiltroRicercaAttoVO } from "@vo/filtro-ricerca-attoVO";
import { PaginazioneVO } from "@vo/paginazioneVO";
import { OrdinamentoVO } from "@vo/ordinamentoVO";
import { AttoVO } from "@vo/attoVO";
import { DatiDecretoManualiVO } from "@vo/dati-decreto-manualiVO";
import { MetadatiRepertorioVO } from "@vo/metadati-repertorioVO";

@Component({
  selector: "app-select-decreto-control",
  templateUrl: "./select-decreto-control.component.html",
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectDecretoControlComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => SelectDecretoControlComponent), multi: true }
  ],
})
export class SelectDecretoControlComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {
  @Input() noDecreto: boolean = false;
  value: AttoVO = null;
  @ViewChild("modaleAggiungiDatiDecreto", { static: false }) modaleAggiungiDatiDecreto: ModaleAggiungiDatiDecretoComponent;
  @Output() decretoModificato = new EventEmitter<void>();

  filtroNumero: string = "";
  filtroAnno: string;
  anniDisponibili: number[] = [];
  erroreDecreto: boolean = false;
  erroreDecretoTesto: string = "";
  disabled: boolean = false;
  private ngControl: NgControl = null;
  private validatorBeforeNoDecreto: ValidatorFn = null;
  private noDecretoApplied: boolean = false;
  private validazioneSubscription: Subscription = null;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private attoService: AttoService, private injector: Injector) {}

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
    var annoCorrente = new Date().getFullYear();
    this.anniDisponibili = [];
    for (var a = annoCorrente; a >= 2020; a--) {
      this.anniDisponibili.push(a);
    }
    this.filtroAnno = String(annoCorrente);
    this.applyNoDecretoState();
  }

  ngOnDestroy() {
    if (this.validazioneSubscription) {
      this.validazioneSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.noDecreto) {
      this.applyNoDecretoState();
    }
  }

  writeValue(value: AttoVO) {
    this.value = value;
    this.erroreDecreto = false;
    this.erroreDecretoTesto = "";

    if (!value || !value.metadatiRepertorio || !value.metadatiRepertorio.numero || value.metadatiRepertorio.numero === "") {
      this.erroreDecreto = true;
      this.erroreDecretoTesto = "Campo obbligatorio";
      return;
    }

    if (value.metadatiRepertorio && value.metadatiRepertorio.numero != null) {
      this.filtroNumero = String(value.metadatiRepertorio.numero).padStart(7, "0");
    }

    var data = (value.metadatiRepertorio && value.metadatiRepertorio.data) || value.dataConclusione;
    if (data) {
      this.filtroAnno = String(new Date(data).getFullYear());
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.noDecreto) {
      return null;
    }

    if (this.value) {
      return null;
    }

    return { required: true };
  }

  get isManuale(): boolean {
    return !!(this.value && !this.value.id);
  }

  get hasDecretoSelezionato(): boolean {
    return !this.noDecreto && !!this.value;
  }

  onNumeroBlur() {
    if (this.filtroNumero) {
      this.filtroNumero = this.filtroNumero.replace(/[^0-9]/g, "");
      if (this.filtroNumero.length > 0 && this.filtroNumero.length <= 7) {
        while (this.filtroNumero.length < 7) {
          this.filtroNumero = "0" + this.filtroNumero;
        }
      }
    }
    this.erroreDecreto = true;
    this.erroreDecretoTesto = "";
    this.triggerValidazioneInterna();
  }

  valida(): Observable<boolean> {
    if (this.noDecreto) {
      this.setDecretoValue(null);
      return of(true);
    }

    if (!this.filtroNumero || !this.filtroAnno) {
      this.erroreDecreto = true;
      this.erroreDecretoTesto = "Campo obbligatorio";
      this.setDecretoValue(null);
      return of(false);
    }

    var filtro: FiltroRicercaAttoVO = new FiltroRicercaAttoVO();
    var dal: Date = new Date();
    dal.setDate(1);
    dal.setMonth(0);
    dal.setFullYear(parseInt(this.filtroAnno, 10));
    dal.setHours(0);
    dal.setMinutes(0);
    dal.setSeconds(0);

    var al: Date = new Date();
    al.setDate(31);
    al.setMonth(11);
    al.setFullYear(parseInt(this.filtroAnno, 10));
    al.setHours(23);
    al.setMinutes(59);
    al.setSeconds(59);

    filtro.limitazioniVisibilita = false;
    filtro.stato = StatoAttoEnum.PUBBLICATO;
    filtro.numeroRepertorio = parseInt(this.filtroNumero, 10);
    filtro.dataRepertoriazioneDal = dal.toISOString();
    filtro.dataRepertoriazioneAl = al.toISOString();

    var paginazione: PaginazioneVO = new PaginazioneVO();
    paginazione.pageNumber = 0;
    paginazione.size = 1;

    var ordinamento: OrdinamentoVO = new OrdinamentoVO();
    ordinamento.elemento = "dataApertura";
    ordinamento.direzione = 0;

    return this.attoService.ricercaAvanzata(filtro, false, paginazione, ordinamento).pipe(
      map((res) => {
        if (res.resultList && res.resultList.length > 0) {
          var decreto = res.resultList[0];
          var decretoSelect = new AttoVO();
          decretoSelect.id = decreto.id;
          decretoSelect.oggetto = decreto.oggetto;
          decretoSelect.metadatiRepertorio = decreto.metadatiRepertorio;
          decretoSelect.dataConclusione = decreto.dataConclusione;
          this.setDecretoValue(decretoSelect);
          this.erroreDecreto = true;
          this.erroreDecretoTesto = "Campo obbligatorio";
          return true;
        }

        if (this.isManuale) {
          this.erroreDecreto = false;
          this.erroreDecretoTesto = "";
          return true;
        }

        this.erroreDecreto = true;
        this.erroreDecretoTesto = "Decreto non trovato";
        this.setDecretoValue(null);
        return false;
      })
    );
  }

  aggiungi() {
    this.modaleAggiungiDatiDecreto.openModal(this.getDatiDecretoManualiCorrenti());
  }

  confermaDatiDecreto(datiDecreto: DatiDecretoManualiVO) {
    var decretoSelect = new AttoVO();
    decretoSelect.oggetto = datiDecreto.oggetto;
    decretoSelect.metadatiRepertorio = new MetadatiRepertorioVO();
    decretoSelect.dataConclusione = datiDecreto.dataConclusione;
    decretoSelect.metadatiRepertorio.numero = datiDecreto.numero;
    decretoSelect.metadatiRepertorio.data = datiDecreto.dataConclusione;
    decretoSelect.metadatiRepertorio.segnatura = datiDecreto.segnatura;
    decretoSelect.id = null;

    this.setDecretoValue(decretoSelect);
    this.onTouched();
    this.decretoModificato.emit();
  }

  onFiltroChange() {
    if (this.value) {
      this.setDecretoValue(null);
    }
    this.erroreDecreto = false;
    this.erroreDecretoTesto = "";
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.updateValueAndValidity({ emitEvent: false });
    }
    if (this.canTriggerValidazione()) {
      this.triggerValidazioneInterna();
    }
    this.decretoModificato.emit();
  }

  rimuoviDecreto() {
    this.setDecretoValue(null);
    this.filtroNumero = "";
    this.filtroAnno = String(new Date().getFullYear());
    this.erroreDecreto = false;
    this.erroreDecretoTesto = "";
    this.onTouched();
    this.decretoModificato.emit();
  }

  get decretoDisplayNumero(): string {
    if (this.value && this.value.metadatiRepertorio && this.value.metadatiRepertorio.numero != null) {
      return String(this.value.metadatiRepertorio.numero).padStart(7, "0");
    }
    return this.filtroNumero || "";
  }

  get decretoDisplayAnno(): string {
    if (this.value) {
      var data = (this.value.metadatiRepertorio && this.value.metadatiRepertorio.data) || this.value.dataConclusione;
      if (data) {
        return String(new Date(data).getFullYear());
      }
    }
    return this.filtroAnno || "";
  }

  private setDecretoValue(value: AttoVO) {
    this.value = value;
    this.onChange(value);
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private getDatiDecretoManualiCorrenti(): DatiDecretoManualiVO {
    if (!this.isManuale || !this.value) {
      return null;
    }

    var dati = new DatiDecretoManualiVO();
    dati.numero = this.value.metadatiRepertorio ? this.value.metadatiRepertorio.numero : null;
    dati.oggetto = this.value.oggetto;
    dati.dataConclusione = (this.value.metadatiRepertorio && this.value.metadatiRepertorio.data) || this.value.dataConclusione;
    return dati;
  }

  private applyNoDecretoState() {
    if (this.noDecreto) {
      if (this.ngControl && this.ngControl.control && !this.noDecretoApplied) {
        this.validatorBeforeNoDecreto = this.ngControl.control.validator;
        this.ngControl.control.clearValidators();
        this.noDecretoApplied = true;
      }
      this.setDecretoValue(null);
      this.filtroNumero = "";
      this.erroreDecreto = false;
      this.erroreDecretoTesto = "";
    } else if (this.ngControl && this.ngControl.control && this.noDecretoApplied) {
      this.ngControl.control.setValidators(this.validatorBeforeNoDecreto);
      this.noDecretoApplied = false;
    }

    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private canTriggerValidazione(): boolean {
    return !this.noDecreto && !!this.filtroAnno && !!this.filtroNumero && this.filtroNumero.length === 7;
  }

  private triggerValidazioneInterna() {
    if (this.validazioneSubscription) {
      this.validazioneSubscription.unsubscribe();
    }
    this.validazioneSubscription = this.valida().subscribe();
  }
}
